import { motion } from "framer-motion";
import { FileSearch, LogOut } from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
interface AuthUser {
  name: string | null;
  email: string;
  avatar_url: string | null;
  status: string;
}

interface Props {
  user?: AuthUser | null;
  onLogout?: () => void;
  error?: string | null;
}

// ─── Google icon (SVG) ────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

// ─── Login screen ─────────────────────────────────────────────────────────────
export default function Login({ user, onLogout, error }: Props) {
  const isPending = user?.status === "pending";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#07091a] px-5">
      {/* Background glow */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(99,102,241,.25),transparent_50%),radial-gradient(ellipse_at_80%_20%,rgba(139,92,246,.15),transparent_50%)]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div className="grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-xl shadow-indigo-500/30">
            <FileSearch size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">ResumeIQ</h1>
            <p className="mt-1 text-sm text-slate-400">AI-powered career intelligence</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-8 shadow-2xl">
          {isPending && user ? (
            // ── Waitlist state ──
            <>
              {user.avatar_url && (
                <img
                  src={user.avatar_url}
                  alt={user.name ?? ""}
                  className="mx-auto mb-4 size-16 rounded-full ring-2 ring-indigo-500/40"
                />
              )}
              <h2 className="mb-1 text-center text-lg font-bold text-white">
                You&apos;re on the waitlist!
              </h2>
              <p className="mb-5 text-center text-sm text-slate-400">
                Thanks {user.name?.split(" ")[0] ?? ""}! Your request has been sent to Srini.
                <br />
                You&apos;ll receive access once approved.
              </p>
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-center text-xs text-amber-300">
                ✦ Access is invite-only. Hang tight!
              </div>
              <button
                onClick={onLogout}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-slate-400 transition hover:border-slate-600 hover:text-white"
              >
                <LogOut size={15} /> Sign out
              </button>
            </>
          ) : (
            // ── Sign-in state ──
            <>
              <h2 className="mb-1 text-center text-lg font-bold text-white">Sign in to continue</h2>
              <p className="mb-6 text-center text-sm text-slate-400">
                Analyse resumes, write cover letters, and prep for interviews — all in one place.
              </p>

              {error && (
                <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-center text-sm text-rose-400">
                  {error === "blocked"
                    ? "Your account has been suspended."
                    : error === "oauth_failed"
                    ? "Google sign-in failed. Please try again."
                    : "Something went wrong. Please try again."}
                </div>
              )}

              <a
                href="/auth/google"
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-600 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 active:bg-slate-100"
              >
                <GoogleIcon />
                Continue with Google
              </a>

              <p className="mt-5 text-center text-xs text-slate-500">
                🔒 Access by invitation only
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
